import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { adminLearningPathsApi } from '@/services/adminLearningPaths';
import { PATHS } from '@/routes/paths';
import { Table, TableBody, TableCell, TableHeaderWrapper, TableRow } from '@/components/ui/table';
import { EmptyState } from '@/components/custom/empty-state';
import { Loader } from '@/components/custom/loader';
import { TiaBanner } from '@/components/custom/tia-banner';
import { DashboardContent } from '@/layouts/dashboard';
import { useSession } from '@/hooks/useSession';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { isTeacherOnly } from '@/lib/teacher-profile';
import { PENSANDO } from '@/pages/dashboard/home-teacher/teacher-character';

import TrilhaPerformanceModal from './TrilhaPerformanceModal';
import TrilhaProgressModal from './TrilhaProgressModal';
import { Pagination, StatusBadge, TitleCell, TrailActions, formatDate, formatPercent } from './TrilhaStudentsShared';

const TABLE_HEAD = [
  { label: 'Trilha' },
  { label: 'Status', width: 150 },
  { label: '% concluído', width: 110 },
  { label: '% dominado', width: 110 },
  { label: 'Início', width: 160 },
  { label: 'Último acesso', width: 160 },
  { label: 'Ações', width: 420 }
];

// Trilha adaptativa: trilhas de um aluno (uma por seleção de matérias), vindo da tela de alunos agrupada.
export default function TrilhaStudentTrails() {
  const { learningPathId, userId } = useParams();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [trilhaName, setTrilhaName] = useState('');
  const [userName, setUserName] = useState(location.state?.userName || '');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [progressTarget, setProgressTarget] = useState(null);
  const [performanceTarget, setPerformanceTarget] = useState(null);
  const { session } = useSession();
  const teacherOnly = isTeacherOnly(session);
  const playfulTeacher = usePlayfulTeacher();

  const load = (page = 1) => {
    setLoading(true);
    return adminLearningPathsApi
      .studentTrails(learningPathId, userId, { page, perPage: 25 })
      .then(json => {
        const payload = json?.data || [];
        const meta = json?.meta || {};
        const items = payload.map(d => ({ id: d.id, ...d.attributes, grouping: d.attributes?.grouping || meta.learning_path?.grouping }));
        setRows(items);
        if (items[0]?.user_name) setUserName(items[0].user_name);
        setTrilhaName(meta.learning_path?.name || '');
        setPagination({
          currentPage: meta.current_page || 1,
          totalPages: meta.total_pages || 1,
          totalCount: meta.total_count || payload.length
        });
      })
      .catch(() => {
        toast.error('Erro ao carregar trilhas do aluno');
        setRows([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningPathId, userId]);

  const breadcrumbs = {
    title: 'Trilhas do aluno',
    nav: [
      { route: PATHS.TRILHAS_ADMIN.LIST, name: teacherOnly ? 'Trilhas' : 'Gerenciar trilhas' },
      { route: PATHS.TRILHAS_ADMIN.STUDENTS(learningPathId), name: trilhaName || 'Alunos' },
      { name: userName || 'Trilhas do aluno', isActive: true }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {/* Playful (professor): banner no padrão da tela de alunos da trilha. */}
      {playfulTeacher && (
        <div className="relative mb-6 sm:mt-16">
          <TiaBanner
            className="h-full"
            image={PENSANDO}
            imageAlt="Personagem do professor"
            imageHeight={190}
            imageOffset={150}
            bubbleCorner="tl"
            bubbleMaxWidth={520}
            text={`Aqui estão as trilhas de ${userName || 'seu aluno'}! Acompanhe o progresso e o desempenho em cada uma delas. 📈`}
          />
        </div>
      )}

      {loading ? (
        <Loader />
      ) : (
        <>
          {rows.length === 0 ? (
            <EmptyState
              type="data"
              variant="plain"
            />
          ) : (
            <Table className="min-w-[1320px]">
              <TableHeaderWrapper tableHead={TABLE_HEAD} />
              <TableBody>
                {rows.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>
                      <TitleCell
                        title={r.title}
                        subtitle={r.learning_objective_name}
                        playful={playfulTeacher}
                      />
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        status={r.status}
                        playful={playfulTeacher}
                      />
                    </TableCell>
                    <TableCell className="text-sm tabular-nums">{formatPercent(r.percent_walked)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{formatPercent(r.percent_mastered)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{formatDate(r.generated_at)}</TableCell>
                    <TableCell className="text-sm tabular-nums">{formatDate(r.last_accessed_at)}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      <TrailActions
                        playful={playfulTeacher}
                        onProgress={() => setProgressTarget(r)}
                        onPerformance={() => setPerformanceTarget(r)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Pagination
            pagination={pagination}
            onChange={load}
            playful={playfulTeacher}
          />
        </>
      )}

      <TrilhaProgressModal
        student={progressTarget}
        open={Boolean(progressTarget)}
        onOpenChange={isOpen => {
          if (!isOpen) setProgressTarget(null);
        }}
      />

      <TrilhaPerformanceModal
        student={performanceTarget}
        open={Boolean(performanceTarget)}
        onOpenChange={isOpen => {
          if (!isOpen) setPerformanceTarget(null);
        }}
      />
    </DashboardContent>
  );
}
